import cn from 'clsx'
import Clickable from 'components/Common/Clickable'
import FormatCurrency from 'components/Common/FormatCurrency'
import SolidCircleIcon from 'LuxKit/Icons/solid/SolidCircleIcon'
import SolidFireIcon from 'LuxKit/Icons/solid/SolidFireIcon'
import StructureLabel from 'LuxKit/Label/StructureLabel'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import Typography from 'LuxKit/Typography/Typography'
import noop from 'lib/function/noop'
import { pluralizeToString } from 'lib/string/pluralize'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import { rem } from 'polished'
import React, { useMemo } from 'react'
import styled from 'styled-components'
import { AccommodationDayView } from './AccommodationCalendarDayPicker'

const Cell = styled(Clickable)`
  position: relative;
  min-height: ${rem(60)};
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${rem(2)};
  padding: ${rem(4)} ${rem(2)} 0 ${rem(2)};
  background-color: ${props => props.theme.palette.neutral.default.eight};
  transition: background-color 0.2s;

  &.type-check-in, &.type-check-out {
    background-color: ${props => props.theme.palette.highlight.primary.normalBackground};
  }

  &.type-between {
    background-color: ${props => props.theme.palette.highlight.primary.lightBackground};
  }

  &.type-none.variant-unavailable {
    color: ${props => props.theme.palette.neutral.default.four};

    ${mediaHoverable} {
      &:hover {
        background-color: ${props => props.theme.palette.neutral.default.eight};
      }
    }
    &:active {
      background-color: ${props => props.theme.palette.neutral.default.eight};
    }
  }
`

const DurationLabel = styled(StructureLabel)`
  position: absolute;
  right: -${rem(20)};
  top: ${rem(6)};
  z-index: 1;
  transform: translateY(-100%);
`

const FeaturedIcon = styled(SolidFireIcon)`
  position: absolute;
  top: 0;
  right: ${rem(2)};
`

const Content = styled(Typography)`
  opacity: 0;

  &.visible {
    opacity: 1;
  }
`

export type AccommodationCalendarDayCellType = 'check-in' | 'check-out' | 'between' | 'none'
export type AccommodationDayCellVariant = 'price-member' | 'price-cheapest' | 'price' | 'none' | 'sold-out-extra-action' | 'unavailable'
export type AccommodationDayCellFeature = 'special' | 'loyalty'

export type AccommodationMobileDayCellSelectHandler = (day: AccommodationDayView) => void

interface Props {
  /** The raw calendar data for a day */
  day: AccommodationDayView;
  /**
   * Event for when the cell is clicked, provides the day object to the listener
   */
  onClick?: AccommodationMobileDayCellSelectHandler;
  /**
   * The purpose the cell is for, determines which components
   * in the cell are shown
   */
  variant: AccommodationDayCellVariant;
  /**
   * The 'state' the cell is currently in
   * Determines mostly how the *cell* looks
   */
  type: AccommodationCalendarDayCellType;
  style?: React.CSSProperties;
  /**
   * The duration of the stay
   */
  duration?: number;
  /**
   * Users current loyalty tier
   * Passed in for performance reasons so each cell isn't listening
   * on state
   */
  tier?: App.LoyaltyTier
}

function AccommodationCalendarSmallDayCell(props: Props) {
  const { day, onClick = noop, variant, type, style, duration, tier } = props

  const priceColour = useMemo<React.ComponentProps<typeof Caption>['colour']>(() => {
    if (variant === 'price-member') {
      return 'lux-plus'
    } else if (variant === 'price-cheapest' && type !== 'check-in') {
      return 'highlight-secondary'
    }
    return 'neutral-two'
  }, [variant, type])

  const showContent = type !== 'between' && type !== 'check-out'

  const price = day.pricing?.price ?? 0

  return <Cell
    style={style}
    disabled={variant === 'unavailable' || (day.soldOut && !day.soldOutExtraActionText)}
    onClick={() => onClick(day)}
    className={cn(
      `type-${type}`,
      `variant-${variant}`,
    )}
    data-testid={`day-${day.date}`}
  >
    {day.featured === 'special' && type !== 'between' && <FeaturedIcon colour="urgency" size="XXS" />}
    {day.featured === 'loyalty' && type !== 'between' && tier && <FeaturedIcon as={SolidCircleIcon} size="XXS" colour={`tier-${tier}`} />}
    {type === 'check-out' && !!duration && <DurationLabel variant="dark">
      {pluralizeToString(day.durationUnit, duration)}
    </DurationLabel>}
    <BodyText variant="medium" align="center">{day.day}</BodyText>
    <Content align="center" className={cn({ visible: showContent })}>
      {(variant === 'price' || variant === 'price-cheapest' || variant === 'price-member') && <Caption
        variant="medium"
        colour={priceColour}
      >
        <FormatCurrency
          value={price}
          format="dynamic"
          notation={price > 10000 ? 'compact' : undefined}
          maximumFractionDigits={price > 10000 ? 1 : undefined}
        />
        {day.conditionalPrice ? '*' : ''}
      </Caption>}
      {variant === 'sold-out-extra-action' && day.soldOutExtraActionText && <Caption
        variant="medium"
        colour="neutral-three"
        underline
      >
        {day.soldOutExtraActionText}
      </Caption>}
    </Content>
  </Cell>
}

export default React.memo(AccommodationCalendarSmallDayCell)
